import React, { useCallback, useEffect, useRef, useState } from 'react';

export type FormatPattern = {
  test: RegExp | ((value: string) => boolean);
  template: string;
};

type AutocompleteInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  formats: FormatPattern[];
  clearInput?: boolean;
};

function resolveTemplate(value: string, formats: FormatPattern[]): string | null {
  for (const { test, template } of formats) {
    const matched = typeof test === 'function' ? test(value) : test.test(value);
    if (matched) return template;
  }
  return null;
}

function computeGhostValue(value: string, formats: FormatPattern[]): string {
  const template = resolveTemplate(value, formats);
  if (!template) return value;
  if (value.length >= template.length) return value;
  return value + template.slice(value.length);
}

const AutocompleteInput = React.forwardRef<HTMLInputElement, AutocompleteInputProps>(
  function AutocompleteInput(
    { formats, clearInput, onChange, defaultValue, className, style, ...props },
    externalRef,
  ) {
    const [localValue, setLocalValue] = useState<string>(
      typeof defaultValue === 'string' ? defaultValue : '',
    );
    const internalRef = useRef<HTMLInputElement | null>(null);

    // Merges our internal ref (for DOM access) with the forwarded ref (RHF's ref)
    const combinedRef = useCallback(
      (el: HTMLInputElement | null) => {
        internalRef.current = el;
        if (typeof externalRef === 'function') externalRef(el);
        else if (externalRef) externalRef.current = el;
      },
      [externalRef],
    );

    useEffect(() => {
      if (clearInput) {
        setLocalValue('');
        if (internalRef.current) internalRef.current.value = '';
      }
    }, [clearInput]);

    const ghostValue = computeGhostValue(localValue, formats);

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      setLocalValue(e.target.value);
      onChange?.(e);
    };

    return (
      <div style={{ position: 'relative' }}>
        {/* Real input — absolute on top, transparent bg so ghost shows through */}
        <input
          {...props}
          ref={combinedRef}
          defaultValue={defaultValue}
          className={className}
          onChange={handleChange}
          style={{
            ...style,
            background: 'transparent',
            position: 'absolute',
            zIndex: 1,
          }}
        />
        {/* Ghost input — in normal flow (establishes height), shows format hint in gray */}
        <input
          aria-hidden="true"
          tabIndex={-1}
          readOnly
          disabled
          className={className}
          value={ghostValue}
          style={{
            ...style,
            color: 'gray',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      </div>
    );
  },
);

export default AutocompleteInput;
